import { DeleteOutlined, MailOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { Button, Popconfirm, Space, Tag } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { type Key } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { type IProductWatchdogAdmin } from '../reducers/watchdog';
import {
  watchdogDeleteMany,
  watchdogLoadAll,
  watchdogSendNow,
} from '../reducers/watchdog/reducer';
import { SPACING } from '../theme/tokens';

interface DataType extends IProductWatchdogAdmin {
  key: string;
}

function CrmWatchdogContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const watchdogs = useSelector((s: RootState) => s.Watchdog.watchdogs);
  const inProcess = useSelector((s: RootState) => s.Watchdog.inProcess);
  const deleteInProcess = useSelector(
    (s: RootState) => s.Watchdog.deleteInProcess,
  );
  const sendInProcess = useSelector((s: RootState) => s.Watchdog.sendInProcess);
  const updatedAt = useSelector((s: RootState) => s.Watchdog.updatedAt);

  const [columns, setColumns] = useState<ColumnsType>([]);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [selectedUUIDs, setSelectedUUIDs] = useState<string[]>([]);

  useEffect((): void => {
    const dataSourceUE: DataType[] | undefined = watchdogs?.map(
      (w: IProductWatchdogAdmin): DataType => ({
        ...w,
        key: w.uuid,
      }),
    );

    const columnsUE: ColumnsType = [
      {
        title: 'Email',
        dataIndex: 'email',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.email, b.email),
        ...getColumnSearch('email'),
      },
      {
        title: 'Product',
        dataIndex: 'productName',
        render: (_: string, record: DataType): React.ReactElement => (
          <Link
            to={`/product/${record.productUUID}`}
            label={record.productName || record.productUUID}
            openInNewTab
          />
        ),
        ...getColumnSearch('productName'),
      },
      {
        title: 'Registered',
        dataIndex: 'createdAt',
        sorter: (a: DataType, b: DataType): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        defaultSortOrder: 'descend',
        render: (createdAt: string): React.ReactElement =>
          getTagByRelativeDate({ date: new Date(createdAt) }),
      },
      {
        title: 'Verified',
        dataIndex: 'isVerified',
        filters: [
          { text: 'Verified', value: true },
          { text: 'Unverified', value: false },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.isVerified === value,
        sorter: (a: DataType, b: DataType): number => {
          if (a.isVerified === b.isVerified) {
            return 0;
          }

          return a.isVerified ? -1 : 1;
        },
        render: (isVerified: boolean): React.ReactElement =>
          isVerified ? (
            <Tag color="green">Verified</Tag>
          ) : (
            <Tag color="red">Unverified</Tag>
          ),
      },
      {
        title: 'Language',
        dataIndex: 'languageTypeCode',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.languageTypeCode, b.languageTypeCode),
        filters: [
          ...new Set(
            dataSourceUE?.map(
              ({ languageTypeCode }: DataType): string => languageTypeCode,
            ),
          ),
        ].map((code: string) => ({ text: code, value: code })),
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.languageTypeCode === value,
      },
    ];

    setColumns(columnsUE);

    if (!dataSourceUE) {
      return;
    }

    setDataSource(dataSourceUE);
  }, [watchdogs]);

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Watchdog"
        onReload={(): void => {
          dispatch(watchdogLoadAll());
        }}
        isReloadDisabled={inProcess}
        isReloadHighlighted={!watchdogs}
        actions={
          <>
            <Popconfirm
              placement="bottom"
              title="Send watchdog emails"
              description="Emails are sent only to verified subscribers whose product is currently in stock. Unverified or out-of-stock rows are skipped."
              onConfirm={(): void => {
                dispatch(watchdogSendNow(selectedUUIDs));
                setSelectedUUIDs([]);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                icon={<MailOutlined />}
                disabled={selectedUUIDs.length === 0 || sendInProcess}
                loading={sendInProcess}
              >
                Send now ({selectedUUIDs.length})
              </Button>
            </Popconfirm>

            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedUUIDs.length === 0 || deleteInProcess}
              loading={deleteInProcess}
              onClick={(): void => {
                dispatch(watchdogDeleteMany(selectedUUIDs));
                setSelectedUUIDs([]);
              }}
            >
              Delete ({selectedUUIDs.length})
            </Button>
          </>
        }
        info={<UpdatedAt value={updatedAt} />}
      />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={inProcess && !watchdogs}
        scroll={{ x: true }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedUUIDs,
          onChange: (keys): void => {
            setSelectedUUIDs(keys as string[]);
          },
        }}
      />
    </Space>
  );
}

export default CrmWatchdogContainer;
